import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { hideBackButton } from '@telegram-apps/sdk-react';
import { BottomNav } from '../components/design/BottomNav';
import { InlineCTA } from '../components/design/InlineCTA';
import { brand, fontFamily, tg } from '../theme/tokens';
import { SCENES } from '../data/scenes';
import { KISS_TYPES, getPrice } from '../data/kissTypes';
import { useWizard } from '../state/wizard';
import type { KissType } from '../lib/api';

interface ResultState {
  videoUrl?: string;
  kissType?: KissType;
  durationSec?: number;
  /** Scene slug (e.g. 'cozy-cafe') — used for background gradient + label. */
  scene?: string;
}

/**
 * Step 6 — Result. Shows the generated video plus quick-actions.
 *
 * Layout (mockup-driven):
 *   - Title block: "💋 Ready" chip + h1 + meta strip with price
 *   - Video stage with scene-colored backdrop + pair-corner thumb
 *   - 2-col secondary action row: Share | Try different style
 *   - InlineCTA "Create another kiss" (fresh flow — clears wizard)
 *
 * Entry points:
 *   - From GeneratingPage on poll-success (full state in navigate.state)
 *   - From LibraryPage when user taps a finished kiss (same shape)
 *
 * "Try different style" preserves photos by keeping the wizard state
 * (DurationStep no longer resets on payment success — see comment there)
 * and routes back to /wizard/scene. If the user landed here from Library
 * after closing+reopening the app, wizard photos are empty — we fall back
 * to navigating home so they re-upload.
 */
export function ResultPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { videoUrl, kissType, durationSec, scene } = (location.state ?? {}) as ResultState;

  const { photo1, photo2, reset } = useWizard();

  useEffect(() => {
    if (!videoUrl) navigate('/', { replace: true });
  }, [videoUrl, navigate]);

  useEffect(() => {
    try { hideBackButton(); } catch { /* */ }
  }, []);

  if (!videoUrl) return null;

  const sceneObj = scene ? SCENES.find((s) => s.id === scene) : null;
  const sceneName = sceneObj?.name ?? 'Custom scene';
  const sceneGradient = sceneObj?.gradient ?? brand.gradient;
  const kissInfo = kissType ? KISS_TYPES.find((k) => k.id === kissType) : null;
  const kissName = kissInfo?.name ?? kissType ?? '';
  const price = kissType && durationSec ? getPrice(kissType, durationSec) : null;

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ url: videoUrl, title: 'My DreamKiss 💋' });
      } else {
        await navigator.clipboard.writeText(videoUrl);
      }
    } catch { /* user cancelled */ }
  };

  // "Try different style" — keep photos, restart scene/kiss/duration. If
  // we don't have photos in memory anymore (page refresh, deep-link from
  // Library), fall back to home so user can re-upload.
  const handleTryDifferent = () => {
    if (photo1 && photo2) {
      navigate('/wizard/scene');
    } else {
      navigate('/');
    }
  };

  // "Create another kiss" — full reset (clears photos too) and back to
  // the start of the wizard. Distinct from "Try different style".
  const handleCreateAnother = () => {
    reset();
    navigate('/', { replace: true });
  };

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', fontFamily, paddingBottom: 180 }}>
      {/* Title block — chip + h1 + meta strip */}
      <div style={{ padding: '16px 20px 8px' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 999,
            background: 'rgba(229, 64, 122, 0.15)',
            color: brand.solid,
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            marginBottom: 8,
          }}
        >
          💋 Ready
        </div>
        <h1
          style={{
            margin: '0 0 4px',
            fontSize: 26,
            fontWeight: 800,
            letterSpacing: -0.4,
            color: tg.text,
          }}
        >
          Your kiss is ready
        </h1>
        <MetaStrip
          sceneName={sceneName}
          kissName={kissName}
          durationSec={durationSec}
          price={price}
        />
      </div>

      {/* Video stage — scene gradient backdrop, pair-corner thumb,
          native browser controls (we don't roll custom controls for MVP
          to keep play/scrub/mute reliable across iOS + Android). */}
      <div
        style={{
          position: 'relative',
          margin: '12px 20px 0',
          aspectRatio: '2 / 3',
          borderRadius: 22,
          overflow: 'hidden',
          background: sceneGradient,
          boxShadow: '0 18px 40px -8px rgba(0, 0, 0, 0.4)',
        }}
      >
        <video
          src={videoUrl}
          controls
          autoPlay
          loop
          playsInline
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'cover',
          }}
        />
        <PairCorner gradient={sceneGradient} />
      </div>

      {/* Action row — 2 columns */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 8,
          padding: '14px 20px 0',
        }}
      >
        <ActionCard
          icon={<UploadIcon />}
          label="Share"
          sub="to friends"
          onClick={handleShare}
        />
        <ActionCard
          icon={<StyleIcon />}
          label="Try different style"
          sub="same photos"
          onClick={handleTryDifferent}
        />
      </div>

      <div style={{ flex: 1 }} />

      <InlineCTA onClick={handleCreateAnother}>Create another kiss</InlineCTA>

      <BottomNav />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
/**
 * Decorative thumb in top-right of the video stage. Mockup uses the same
 * gradient as the stage background — visually it reads as "the input pair
 * that produced this video". When we have real pair preview frames stored
 * server-side, swap `background` for a background-image URL.
 */
function PairCorner({ gradient }: { gradient: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        top: 12,
        right: 12,
        width: 60,
        aspectRatio: '3 / 4',
        borderRadius: 10,
        overflow: 'hidden',
        border: '2px solid rgba(255, 255, 255, 0.7)',
        background: gradient,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        pointerEvents: 'none',
      }}
    >
      {/* Expand glyph — hints "tap to see the inputs". Currently
          decorative; future feature: tap opens lightbox with both photos. */}
      <div
        style={{
          position: 'absolute',
          top: 4,
          right: 4,
          width: 18,
          height: 18,
          borderRadius: 999,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          lineHeight: 1,
        }}
      >
        ⤢
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: 4,
          left: 4,
          right: 4,
          fontSize: 8,
          fontWeight: 700,
          color: '#fff',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.6)',
          textAlign: 'center',
          letterSpacing: 0.5,
        }}
      >
        PAIR
      </div>
    </div>
  );
}

function ActionCard({
  icon,
  label,
  sub,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  sub: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        appearance: 'none',
        padding: '10px 8px',
        borderRadius: 14,
        background: tg.bg2,
        border: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        color: tg.text,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily,
      }}
    >
      <div style={{ color: brand.solid, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </div>
      {label}
      <span style={{ fontSize: 10, color: tg.textHint, fontWeight: 500 }}>{sub}</span>
    </button>
  );
}

/**
 * Inline meta strip under the title — joins the non-empty meta tokens with
 * mid-dots. Split out to a component so the conditional rendering reads
 * cleanly (a "filter-and-map" inline returned an awkward heterogenous
 * array — strings + JSX — that TS complained about).
 */
function MetaStrip({
  sceneName,
  kissName,
  durationSec,
  price,
}: {
  sceneName: string;
  kissName: string;
  durationSec?: number;
  price: number | null;
}) {
  const tokens: React.ReactNode[] = [];
  if (sceneName) tokens.push(<span key="scene">{sceneName}</span>);
  if (kissName) tokens.push(<span key="kiss">{kissName}</span>);
  if (durationSec) tokens.push(<span key="dur">{durationSec}s</span>);
  if (price !== null) {
    tokens.push(
      <span key="price" style={{ color: '#E89627', fontWeight: 700 }}>
        ★ {price}
      </span>,
    );
  }
  return (
    <div style={{ fontSize: 13, color: tg.textHint, fontWeight: 500 }}>
      {tokens.map((t, i) => (
        <span key={i}>
          {t}
          {i < tokens.length - 1 && <span style={{ margin: '0 6px', opacity: 0.5 }}>·</span>}
        </span>
      ))}
    </div>
  );
}

function UploadIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

function StyleIcon() {
  // Three horizontal lines — "menu / options" style. The mockup used this
  // for "Try different style" suggesting "more configurations to choose".
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}
